import { storeDatepickerDefaults } from '@/src/utils/datepicker-defaults.service'
import { useUpdateEffect } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import {
  adjustDateByOneUnit,
  getEndDate,
  getPickerTypeProps,
  getPopperModifiers,
  getPossibleDate,
  getStartDate,
  offsetDateBasedOnPickerType,
  parseManualInput,
} from './datePicker.helpers'
import type { DatePickerType } from './datePicker.types'

interface BaseDatePickerProps {
  pickerType: DatePickerType
  minDate?: Date | string
  maxDate?: Date | string
  monthsShown?: number
  allowSameDateSelection?: boolean
  datePickerRef?: React.RefObject<HTMLInputElement>
  weekPrefix?: string
  /** When true (single-date pickers only), the calendar popover opens on first mount — use in pill/popover UIs so the user does not click the input first. */
  startWithCalendarOpen?: boolean
}

interface RangeDatePickerProps extends BaseDatePickerProps {
  isRange: true
  value?: [string | Date | null, string | Date | null]
  initialValue?: [string | Date | null, string | Date | null]
  onChange?: (dateRange: [Date | null, Date | null]) => void
  shouldStoreState?: boolean
}

interface SingleDatePickerProps extends BaseDatePickerProps {
  isRange?: false | undefined
  value?: string | Date | null
  initialValue?: string | Date | null
  datePickerRef?: React.RefObject<HTMLInputElement>
  onChange?: (date: Date | null) => void
}

type UseDatePickerProps = RangeDatePickerProps | SingleDatePickerProps

export function useDatePicker(props: UseDatePickerProps) {
  const offsetMinDate = props.minDate ? offsetDateBasedOnPickerType(props.pickerType, new Date(props.minDate), 'start') : undefined
  const offsetMaxDate = props.maxDate ? offsetDateBasedOnPickerType(props.pickerType, new Date(props.maxDate), 'end') : undefined
  const minDate = useMemo(() => offsetMinDate, [offsetMinDate?.toString()])
  const maxDate = useMemo(() => offsetMaxDate, [offsetMaxDate?.toString()])

  const popperModifiers = getPopperModifiers()
  const pickerTypeProps = getPickerTypeProps(props.pickerType, props.weekPrefix)
  const [isOpen, setIsOpen] = useState(() => !props.isRange && props.startWithCalendarOpen === true)

  const [startDate, setStartDate] = useState(() => {
    if (props.isRange) {
      const startDateValue = props.value?.[0] === null ? null : props.value?.[0] ?? props.initialValue?.[0] ?? undefined
      const endDateValue = props.value?.[1] === null ? null : props.value?.[1] ?? props.initialValue?.[1] ?? undefined
      return getStartDate(props.pickerType, [startDateValue, endDateValue], [minDate, maxDate], props.allowSameDateSelection)
    }
    return getPossibleDate(props.value ?? (props.initialValue as string | Date), maxDate, minDate)
  })

  const [endDate, setEndDate] = useState(() => {
    const endDateValue = props.value?.[1] === null ? null : props.value?.[1] ?? props.initialValue?.[1] ?? undefined
    if (props.isRange) {
      return getEndDate(props.pickerType, [startDate, endDateValue], [minDate, maxDate], props.allowSameDateSelection)
    }
    return null
  })

  const updateDatepickerDefaults = (startDate, endDate) => {
    if (props.isRange && props.shouldStoreState && startDate && endDate) {
      storeDatepickerDefaults(startDate, endDate)
    }
  }

  const [monthsShown, setMonthsShown] = useState(props.monthsShown)

  // date time picker state variables
  const [selectedTime, setSelectedTime] = useState({ hours: '00', minutes: '00' })
  const [isFinalSelection, setIsFinalSelection] = useState(!props.isRange)

  // raw input (user writing manually a date)
  const [rawStartInput, setRawStartInput] = useState<string | null>(null)
  const [rawEndInput, setRawEndInput] = useState<string | null>(null)

  const setDates = (dates: [Date | null, Date | null]) => {
    const areStartDatesDifferent = dates?.[0]?.toString() !== startDate?.toString()
    const areEndDatesDifferent = dates?.[1]?.toString() !== endDate?.toString()
    if (areStartDatesDifferent) setStartDate(dates?.[0])
    if (areEndDatesDifferent) setEndDate(dates?.[1])

    return [areStartDatesDifferent, areEndDatesDifferent]
  }

  // Makes sure the calendar starts in the available min/max date range view
  useEffect(() => {
    if (!props.isRange || isOpen) return
    if (props.value?.[0] && props.value?.[1]) {
      const newStartDate = getStartDate(props.pickerType, props.value, [minDate, maxDate], props.allowSameDateSelection)
      const newEndDate = getEndDate(
        props.pickerType,
        [newStartDate, props.value?.[1] ?? null],
        [minDate, maxDate],
        props.allowSameDateSelection
      )
      setDates([newStartDate, newEndDate])
    }
  }, [props.value?.[0], props.value?.[1]])

  useEffect(() => {
    if (props.isRange || isOpen) return
    const singleDate = getPossibleDate(props.value, maxDate, minDate)
    setStartDate(singleDate)
  }, [props.value])

  // // Trigger onChange when min/max dates change
  useUpdateEffect(() => {
    if (props.isRange) {
      const newStartDate = getStartDate(props.pickerType, props.value, [minDate, maxDate], props.allowSameDateSelection)
      const newEndDate = getEndDate(
        props.pickerType,
        [newStartDate, props.value?.[1] ?? null],
        [minDate, maxDate],
        props.allowSameDateSelection
      )
      const [areStartDatesDifferent, areEndDatesDifferent] = setDates([newStartDate, newEndDate])
      if (areStartDatesDifferent || areEndDatesDifferent) {
        props.onChange?.([newStartDate, newEndDate])
      }
    } else {
      const newDate = getPossibleDate(props.value, maxDate, minDate)
      if (newDate?.toString() !== startDate?.toString()) {
        setStartDate(newDate)
        props.onChange?.(newDate)
      }
    }
  }, [minDate, maxDate])

  const handleChange = (
    value: Date | null | [Date | null, Date | null],
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (event && event.type !== 'click') {
      return
    }

    if (!Array.isArray(value)) {
      if (props.isRange) return

      const clickedDate = value
      if (clickedDate === null) {
        setStartDate(null)
        props.onChange?.(null)
        return
      }

      if (props.pickerType === 'datetime') {
        setStartDate(clickedDate)
        return
      }

      const newDate = offsetDateBasedOnPickerType(props.pickerType, clickedDate, 'start')
      setStartDate(newDate)
      props.onChange?.(newDate)
      setIsOpen(false)
    } else {
      if (!props.isRange) return

      const [date1, date2] = value

      if (props.pickerType === 'datetime') {
        if (!isFinalSelection) {
          if (date1?.toString() === startDate?.toString()) {
            setStartDate(date2 ?? date1)
          } else {
            setStartDate(date1)
          }
        } else {
          if (date2?.toString() === endDate?.toString()) {
            setEndDate(date1 ?? date2)
          } else {
            setEndDate(date2 ?? date1)
          }
        }
        return
      }

      let newStartDate: Date | null = null
      if (date1) {
        newStartDate = offsetDateBasedOnPickerType(props.pickerType, date1, 'start')
      }
      setStartDate(newStartDate)

      let newEndDate: Date | null = null
      if (date2) {
        newEndDate = offsetDateBasedOnPickerType(props.pickerType, date2, 'end')
      }
      setEndDate(newEndDate)

      props.onChange?.([newStartDate, newEndDate])
      updateDatepickerDefaults(newStartDate, newEndDate)
      if (!isFinalSelection) setIsFinalSelection(true)
      else {
        setIsFinalSelection(false)
        setIsOpen(false)
      }
    }
  }

  const handleManualInput = (e?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    const target = e?.target as HTMLInputElement
    if (!target?.value && target?.value !== '') return

    const result = parseManualInput(
      target.value,
      props.pickerType,
      !!props.isRange,
      [startDate, endDate],
      minDate,
      maxDate,
      props.weekPrefix
    )

    setRawStartInput(result.rawInput)
    if (props.isRange) {
      const [startStr, endStr] = result.rawInput.split('-').map(s => s.trim())
      setRawStartInput(startStr)
      setRawEndInput(endStr || '')
    }
  }

  const handleTimeSelect = (time: { hours: string; minutes: string }) => {
    setSelectedTime(time)
    const dateToUpdate = props.isRange ? (isFinalSelection ? endDate || new Date() : startDate) : startDate
    if (dateToUpdate) {
      const newDate = new Date(dateToUpdate)
      newDate.setHours(Number.parseInt(time.hours))
      newDate.setMinutes(Number.parseInt(time.minutes))
      if (!props.isRange) {
        setStartDate(newDate)
      } else if (isFinalSelection) {
        setEndDate(newDate)
      } else {
        setStartDate(newDate)
      }
    }
  }

  const handleDateTimeConfirm = () => {
    if (!props.isRange) {
      if (!startDate) return
      props.onChange?.(startDate)
      setIsOpen(false)
      return
    }

    if (!isFinalSelection) {
      setIsFinalSelection(true)
      setSelectedTime({ hours: '00', minutes: '00' })
      if (!startDate) return
      props.onChange?.([startDate, null])
      if (endDate && endDate < startDate) {
        // Make sure the end date is not before the start date
        setEndDate(adjustDateByOneUnit(startDate, props.pickerType, true))
      }
    } else {
      setIsFinalSelection(false)
      props.onChange?.([startDate, endDate])
      setIsOpen(false)
    }
  }

  const validateAndSetRawInputDates = () => {
    if (!rawStartInput) {
      setRawStartInput('')
      setRawEndInput('')
      return
    }

    const inputValue = props.isRange && rawEndInput ? `${rawStartInput} - ${rawEndInput}` : rawStartInput

    const result = parseManualInput(inputValue, props.pickerType, !!props.isRange, [startDate, endDate], minDate, maxDate)
    const [newStartDate, newEndDate] = result.dateRange

    if (props.isRange) {
      if (newStartDate && newEndDate) {
        setStartDate(newStartDate)
        setEndDate(newEndDate)
        props.onChange?.([newStartDate, newEndDate])
      }
    } else if (newStartDate) {
      setStartDate(newStartDate)
      props.onChange?.(newStartDate)
    }

    // Reset raw inputs
    setRawStartInput('')
    setRawEndInput('')
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && isOpen) {
      validateAndSetRawInputDates()
      setIsOpen(false)
    }
  }

  const handleCalendarClose = () => {
    setIsFinalSelection(!props.isRange)

    if (!props.isRange && props.pickerType === 'datetime' && startDate) {
      props.onChange?.(startDate)
    }

    // for some reason this works as a hack workaround to make showing multiple months not shift every time you open the calendar.
    // hack fix gotten from https://github.com/Hacker0x01/react-datepicker/issues/3942#issuecomment-1483569905
    setTimeout(() => {
      setMonthsShown(1)
      setTimeout(() => setMonthsShown(props.monthsShown), 1)
    }, 1)
  }

  return {
    isOpen,
    setIsOpen,
    startDate,
    endDate,
    selectedTime,
    isFinalSelection,
    minDate,
    maxDate,
    monthsShown,
    popperModifiers,
    dateFormat: pickerTypeProps.dateFormat,
    showTimeSelect: pickerTypeProps.showTimeSelect,
    timeFormat: pickerTypeProps.timeFormat,
    showYearPicker: pickerTypeProps.showYearPicker,
    showMonthYearPicker: pickerTypeProps.showMonthYearPicker,
    showQuarterYearPicker: pickerTypeProps.showQuarterYearPicker,
    isRange: props.isRange,
    handleTimeSelect,
    handleDateTimeConfirm,
    handleManualInput,
    handleChange,
    handleCalendarClose,
    handleKeyPress,
  }
}
